import type { KeyboardEvent } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FUNNEL_STAGE_LABELS,
  formatDateTime,
  PersonDisplay,
} from '@dataroom/shared';
import type { BuyerEngagementSummary } from '@dataroom/shared';
import {
  accessEndedLabel,
  byMostRecentActivity,
  countLabel,
  openedOfVisibleLabel,
} from '@/lib/engagement';
import { buyerLabel } from '@/lib/buyer-pipeline';

interface BuyerEngagementCardProps {
  buyers: BuyerEngagementSummary[];
  loading: boolean;
  error: string | null;
  /** Open that buyer's detail (access + engagement). Rows are read-only without it. */
  onSelectBuyer?: (actorId: string) => void;
}

/**
 * ADR-0038 — the room-level comparison of buyers: what each has done, side by
 * side, as plain facts from the audit log. Listed by most recent activity
 * (ADR-0056) — an order by WHEN, the newest first, buyers who have done nothing
 * yet last; nothing on it is a score, a ranking or a judgment.
 */
export function BuyerEngagementCard({
  buyers,
  loading,
  error,
  onSelectBuyer,
}: BuyerEngagementCardProps) {
  // Named like the pipeline: the display name (person or firm), else the email.
  const rows = [...buyers].sort((a, b) => byMostRecentActivity(a, b, buyerLabel));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Buyer activity</CardTitle>
        <p className="text-sm text-muted-foreground">
          What each buyer has opened and when, from the room’s audit log, most recent activity
          first. “Of what they can see” counts the documents their access shows them today.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 font-medium">Buyer</th>
                  <th className="py-2 font-medium">Stage</th>
                  <th className="py-2 font-medium">Last active</th>
                  <th className="py-2 font-medium">Documents opened</th>
                  <th className="py-2 font-medium">Views · downloads</th>
                  <th className="py-2 font-medium">Of what they can see</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr
                    key={b.actorId}
                    className={`border-b last:border-0 ${
                      onSelectBuyer
                        ? 'cursor-pointer hover:bg-muted/50 focus-within:bg-muted/50'
                        : ''
                    }`}
                    {...(onSelectBuyer
                      ? {
                          role: 'button',
                          tabIndex: 0,
                          'aria-label': `Open activity for ${buyerLabel(b)}`,
                          onClick: () => onSelectBuyer(b.actorId),
                          onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onSelectBuyer(b.actorId);
                            }
                          },
                        }
                      : {})}
                  >
                    <td className="py-2 pr-4 font-medium">
                      <PersonDisplay
                        name={b.actorName}
                        email={b.actorEmail}
                        fallback={b.actorId}
                        size="sm"
                      />
                      {accessEndedLabel(b) ? (
                        <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                          {accessEndedLabel(b)}
                        </p>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4">{FUNNEL_STAGE_LABELS[b.stage]}</td>
                    <td className="py-2 pr-4">{formatDateTime(b.lastActiveAt, 'Not yet')}</td>
                    <td className="py-2 pr-4">{b.documentsOpened}</td>
                    <td className="py-2 pr-4">
                      {countLabel(b.views, 'view')} · {countLabel(b.downloads, 'download')}
                    </td>
                    <td className="py-2 pr-4">{openedOfVisibleLabel(b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No buyers yet. Activity shows here once a buyer is invited.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
